import ChartTooltip from "@ui/ChartTooltip";
import { LineChart } from "echarts/charts";
import { GridComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import ReactEcharts from "echarts-for-react";
import { TFunction } from "i18next";
import { Ref, useImperativeHandle, useRef } from "react";
import { renderToString } from "react-dom/server";
import { useTranslation } from "react-i18next";

import { CategoryStyles, DisplayTypes } from "@/config";
import { Comparison, DataPoint } from "@/types";
import { formatCarbon, getCarbonDisplayUnit } from "@/utils/carbon";
import { encodeSvgToDataURI, SYMBOL_SVG } from "@/utils/chart";
import { extractHeaders, extractSeries } from "@/utils/comparison";

import ComparisonLoading from "./ComparisonLoading";

echarts.use([GridComponent, TooltipComponent, LineChart, CanvasRenderer]);

function optionFactory(data: Comparison, type: DisplayTypes, t: TFunction) {
  const headers = extractHeaders(data);
  const rows = extractSeries(data.columns, type);

  // Get the best unit
  const rowsUnit = getCarbonDisplayUnit(
    rows.flat().map((rowItem) => rowItem.carbon)
  );

  // Convert all the carbon values to the provided unit
  // and store the localized carbon string.
  const formattedRows = rows.map((row) =>
    row.map((rowItem) => {
      const { formattedCarbon, convertedCarbon } = formatCarbon(
        t,
        rowItem.carbon,
        rowsUnit
      );
      return {
        ...rowItem,
        carbon: convertedCarbon,
        carbonToDisplay: formattedCarbon,
      };
    })
  ) satisfies Array<Array<DataPoint & { carbonToDisplay: string }>>;

  return {
    textStyle: {
      fontFamily: "Poppins",
    },
    xAxis: {
      data: headers,
      boundaryGap: false,
      axisLine: {
        lineStyle: {
          type: "dashed",
        },
      },
    },
    yAxis: {
      position: "right",
      axisLabel: {
        formatter: (value: number) =>
          `${t("formatter:number", { value })} ${t(`common:unit.${rowsUnit}`)}`,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
        },
      },
    },
    tooltip: {
      borderWidth: 0,
      padding: 0,
      position: "right",
      backgroundColor: "transparent",
      formatter: ({
        seriesName,
        name,
        color,
        data,
      }: {
        seriesName: string;
        name: string;
        color: string;
        data: { value: number; valueToDisplay: string };
      }) => {
        const effectiveSeriesName =
          seriesName === "total" ? name : t(`reviewCategories:${seriesName}`);

        return renderToString(
          <ChartTooltip
            seriesName={effectiveSeriesName}
            valueToDisplay={data.valueToDisplay}
            unit={t(`common:unit.${rowsUnit}`)}
            color={color}
            Icon={CategoryStyles[seriesName].Icon}
            iconClassName={seriesName === "total" ? "hidden" : ""}
          />
        );
      },
    },
    series: formattedRows.map((row) => ({
      name: row[0].key,
      smooth: true,
      color: CategoryStyles[row[0].key]?.color,
      data: row.map(({ carbon, carbonToDisplay }) => ({
        value: carbon,
        valueToDisplay: carbonToDisplay,
      })),
      type: "line",
      symbol: encodeSvgToDataURI(SYMBOL_SVG, CategoryStyles[row[0].key]?.color),
      symbolSize: 12,
      lineStyle: {
        color: CategoryStyles[row[0].key]?.color,
      },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {
            offset: 0,
            // here {CategoryStyles[row[0].key]?.color} is the hexadecimal code of the color (RGB format), followed by an alpha value, it becomes an RGBA format
            color: `${CategoryStyles[row[0].key]?.color}4D`, // opacity 77% (4D base-16 = 77 base-10)
          },
          {
            offset: 1,
            color: `${CategoryStyles[row[0].key]?.color}0D`, // opacity 13% (0D base-16 = 13 base-10)
          },
        ]),
      },
      stack: "x",
      emphasis: {
        disabled: type === DisplayTypes.total,
        focus: "series",
      },
      triggerLineEvent: true,
    })),
  };
}

type ComparisonChartProps = {
  data: Comparison;
  type: DisplayTypes;
  onHover: (name: string | undefined) => void;
  ref?: Ref<ComparisonChartRef>;
};

export type ComparisonChartRef = {
  highlight: (name: string | undefined) => void;
};

function ComparisonChart({ data, type, onHover, ref }: ComparisonChartProps) {
  const echartsRef = useRef<ReactEcharts>(null);
  const { t } = useTranslation(["formatter", "common", "reviewCategories"]);

  useImperativeHandle(ref, () => {
    return {
      highlight(name: string | undefined) {
        if (echartsRef.current) {
          echartsRef.current.getEchartsInstance().dispatchAction({
            type: "highlight",
            seriesName: name,
          });
        }
      },
    };
  }, []);

  if (!data) {
    return <ComparisonLoading />;
  }

  const option = optionFactory(data, type, t);

  const chart = (
    <ReactEcharts
      echarts={echarts}
      option={option}
      notMerge={true}
      ref={echartsRef}
      onEvents={{
        mouseover: ({ seriesName }: { seriesName: string }) => {
          onHover(seriesName);
        },
        mouseout: () => {
          onHover(undefined);
        },
      }}
    />
  );

  return chart;
}

ComparisonChart.displayName = "ComparisonChart";

export default ComparisonChart;
